import React, { useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import { Box, Dialog, Typography } from '@mui/material';
import { API } from '../../../../../api';
import { getLLAuthHeaders } from '../../../utils/llAuthHeaders';

export const INVITE_FINISH_GATE_COPY = 'Set an audience or invite someone by name to finish.';

export const MEMBERS_STEP_COPY =
  'Choose who this program is for. People in the audience are enrolled when you click Enroll People; anyone you enroll by name is invited straight away.';

interface ConfirmState {
  open: boolean;
  // Who Invite & Finish will enrol right now; null while checking.
  count: number | null;
  audienceSet: boolean;
}

const CLOSED: ConfirmState = { open: false, count: null, audienceSet: true };

/**
 * Invite & Finish (creation flow §6), shared by the AI wizard's Members step and
 * the Manual / Mentorship Members step: confirm with a live match count, then
 * enrol the stored audience. The BE marks the program Invited either way, so
 * finishing with nobody matched yet is a real outcome, not an error.
 */
export const useInviteAndFinish = (groupId: string | null | undefined, onFinished: () => void) => {
  const [confirm, setConfirm] = useState<ConfirmState>(CLOSED);
  const [finishing, setFinishing] = useState(false);

  const openConfirm = useCallback(async () => {
    setConfirm({ open: true, count: null, audienceSet: true });
    if (!groupId) return;
    try {
      // No body rules → the BE evaluates the audience STORED on the group,
      // computed fresh now. toAdd = who Invite & Finish will actually enrol.
      const res: any = await API.previewProgramAudience({ id: groupId, ...getLLAuthHeaders() });
      const data = res?.data?.data ?? res?.data ?? {};
      setConfirm({
        open: true,
        count: typeof data.toAdd === 'number' ? data.toAdd : 0,
        audienceSet: data.audienceSet !== false
      });
    } catch (err) {
      console.error('[InviteFinish] audience preview failed', err);
      setConfirm({ open: true, count: 0, audienceSet: true });
    }
  }, [groupId]);

  const cancel = useCallback(() => {
    if (!finishing) setConfirm(CLOSED);
  }, [finishing]);

  const finish = useCallback(async () => {
    if (!groupId) {
      toast.error('This program could not be finished. Please refresh the page and try again.');
      return;
    }
    setFinishing(true);
    try {
      const res: any = await API.enrollProgramAudience({ id: groupId, ...getLLAuthHeaders() });
      const data = res?.data?.data ?? res?.data ?? {};
      const enrolled = typeof data.enrolled === 'number' ? data.enrolled : 0;
      toast.success(
        enrolled > 0
          ? `${enrolled} ${enrolled === 1 ? 'person' : 'people'} enrolled in the program.`
          : confirm.audienceSet
            ? 'Program is live. Anyone who matches the audience later is added automatically.'
            : 'Program is live.'
      );
      setConfirm(CLOSED);
      onFinished();
    } catch (err: any) {
      console.error('[InviteFinish] audience enroll failed', err);
      toast.error(err?.response?.data?.message || 'The program could not be published. Please try again.');
    } finally {
      setFinishing(false);
    }
  }, [groupId, onFinished, confirm.audienceSet]);

  return {
    finishing,
    openConfirm,
    dialogProps: {
      open: confirm.open,
      count: confirm.count,
      audienceSet: confirm.audienceSet,
      finishing,
      onCancel: cancel,
      onConfirm: finish
    }
  };
};

const InviteFinishDialog: React.FC<{
  open: boolean;
  count: number | null;
  audienceSet: boolean;
  finishing: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ open, count, audienceSet, finishing, onCancel, onConfirm }) => {
  const checking = count === null;
  const invitesAudience = audienceSet && !checking && count > 0;
  return (
    <Dialog
      open={open}
      onClose={() => (finishing ? undefined : onCancel())}
      PaperProps={{
        sx: {
          background: '#1A1A1A',
          borderRadius: '12px',
          border: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)',
          p: 4,
          maxWidth: 460,
          width: '100%'
        }
      }}
    >
      <Typography sx={{ fontSize: 20, fontWeight: 700, fontFamily: 'Inter', color: 'var(--Text-text-primary, #FDFDFD)', mb: 2 }}>
        Enroll people
      </Typography>
      <Typography sx={{ fontSize: 14, fontFamily: 'Inter', color: 'var(--Text-text-secondary, #B6B6B6)', lineHeight: '22px', mb: 3 }}>
        {checking ? (
          'Checking who matches the selected audience...'
        ) : !audienceSet ? (
          'No audience is set, so only the people you enrolled by name will have access. The program goes live when you finish.'
        ) : invitesAudience ? (
          <>
            You're about to enroll <strong>{count}</strong> {count === 1 ? 'person' : 'people'} from the selected
            Continue?
          </>
        ) : (
          'Nobody matches the audience yet. The program still goes live, and anyone who matches later is added automatically.'
        )}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1.5 }}>
        <Box
          component="button"
          onClick={onCancel}
          disabled={finishing}
          sx={{
            flex: 1,
            background: '#1C1C1C',
            border: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)',
            borderRadius: '8px',
            px: 2,
            py: 1.5,
            cursor: finishing ? 'default' : 'pointer',
            color: finishing ? '#565656' : 'var(--Text-text-primary, #FDFDFD)',
            fontFamily: 'Inter',
            fontSize: 14,
            fontWeight: 500,
            '&:hover': { background: finishing ? '#1C1C1C' : '#262626' }
          }}
        >
          Cancel
        </Box>
        <Box
          component="button"
          onClick={onConfirm}
          disabled={finishing || checking}
          sx={{
            flex: 1,
            // Dark while checking/finishing, orange once it can be clicked.
            background:
              finishing || checking ? 'rgba(0, 0, 0, 0.2)' : 'linear-gradient(180deg, #F79420 0%, #F15A26 100%)',
            boxShadow: finishing || checking ? 'inset 0 0 0 1px #2A2A2A' : 'none',
            border: 'none',
            borderRadius: '8px',
            px: 2,
            py: 1.5,
            cursor: finishing || checking ? 'default' : 'pointer',
            color: finishing || checking ? '#565656' : '#FDFDFD',
            fontFamily: 'Inter',
            fontSize: 14,
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            '&:hover': { opacity: finishing || checking ? 1 : 0.9 }
          }}
        >
          {finishing
            ? invitesAudience
              ? 'Enrolling...'
              : 'Finishing...'
            : invitesAudience || checking
            ? 'Enroll People'
            : 'Finish'}
        </Box>
      </Box>
    </Dialog>
  );
};

export default InviteFinishDialog;
